import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card, Row } from 'react-bootstrap';
import { useBrand } from '../../context/AuthContext';
import { useBenefitGrantRecalculation } from './hooks/useBenefitGrantRecalculation';
import { getFundTypeLabel } from '../../utils/fundTypeUtils';
import { FUND_TYPES, BENEFIT_STATUS } from '../../constants/constants';
import CustomerInfoPanel from '../benefit/CustomerInfoPanel';
import AgencyInfoPanel from '../benefit/AgencyInfoPanel';
import CurrentBenefitInfoPanel from './CurrentBenefitInfoPanel';
import ModifyBenefitFundTypeRenderer from './ModifyBenefitFundTypeRenderer';
import AdditionalInformationField from '../benefit/fields/AdditionalInformationField';
import Required from '../../utils/Required';

const ModifyBenefitModal = ({ benefit, onClose, onSave }) => {
  const { isCNG } = useBrand();
  const [validated, setValidated] = useState(false);
  const fundType = benefit.fundType;
  const arrearage = benefit.arrearage;
  const priorGrantAmount = benefit.priorPledgeAmount;

  const [status, setStatus] = useState('');
  const [submissionType, setSubmissionType] = useState(benefit.submissionType || 'NEW');
  const [pledgeAmount, setPledgeAmount] = useState(benefit.pledgeAmount || '');
  const [additionalInformation, setAdditionalInformation] = useState(benefit.additionalInformation || '');
  const [reasonForChange, setReasonForChange] = useState(benefit.reasonForChange || '');
  const [incomeMeasurementType, setIncomeMeasurementType] = useState('');
  const [incomeMeasurementPercentage, setIncomeMeasurementPercentage] = useState('');
  const [arrearsLiabilityAmount, setArrearsLiabilityAmount] = useState(benefit.arrearsLiabilityAmount || '');

  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);


  useBenefitGrantRecalculation({
    fundType,
    arrearsLiabilityAmount,
    arrearage,
    priorGrantAmount,
    setPledgeAmount,
  });

  useEffect(() => {
    if (benefit.fpl !== null && benefit.fpl !== undefined) {
      setIncomeMeasurementType('FPL');
      setIncomeMeasurementPercentage(benefit.fpl);
    } else if (benefit.smi !== null && benefit.smi !== undefined) {
      setIncomeMeasurementType('SMI');
      setIncomeMeasurementPercentage(benefit.smi);
    } else if (benefit.ami !== null && benefit.ami !== undefined) {
      setIncomeMeasurementType('AMI');
      setIncomeMeasurementPercentage(benefit.ami);
    }
  }, [benefit]);

  const handleFieldChange = (field, value) => {
    switch (field) {
      case 'pledgeAmount':
        setPledgeAmount(value);
        break;
      case 'incomeMeasurementType':
        setIncomeMeasurementType(value);
        break;
      case 'incomeMeasurementPercentage':
        setIncomeMeasurementPercentage(value);
        break;
      case 'additionalInformation':
        setAdditionalInformation(value);
        break;
      case 'arrearsLiabilityAmount':
        setArrearsLiabilityAmount(value);
        break;
      default:
        return;
    }

    setHasChanges(true);
  };

  const showIncomeMeasurement = isCNG && fundType === FUND_TYPES.OTHER;

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }

    // If HTML5 validation fails, browser NEVER calls this
    setErrors([]);
    setSaving(true);

    const updateData = {
      id: benefit.id,
      status: status,
      submissionType: submissionType,
      pledgeAmount: pledgeAmount ? parseFloat(pledgeAmount) : null,
      additionalInformation: additionalInformation,
      reasonForChange: reasonForChange
    };

    if (showIncomeMeasurement && incomeMeasurementType) {
      updateData.incomeMeasurementType = incomeMeasurementType;
      updateData.incomeMeasurementPercentage = incomeMeasurementPercentage ? parseInt(incomeMeasurementPercentage) : null;
    }

    if (arrearsLiabilityAmount && pledgeAmount) {
      updateData.arrearsLiabilityAmount = arrearsLiabilityAmount;
      updateData.pledgeAmount = pledgeAmount;
    }

    const result = await onSave(benefit.id, updateData);

    if (!result.success) {
      setErrors(result.errors || ['Failed to update benefit.']);
      setSaving(false);
    }
  };


  const getApproveButtonText = () => hasChanges ? 'Approve (with changes)' : 'Approve';
  const getHoldButtonText = () => hasChanges ? 'Hold (with changes)' : 'Hold';

  return (
    <Modal show={true} onHide={onClose} size="lg" backdrop="static" centered>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Modify {getFundTypeLabel(benefit.fundType)} Benefit - {benefit.accountId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant="danger">
              <ul className="mb-0">
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </Alert>
          )}
          <Card className="bg-secondary-soft">
            <Card.Body>
              <Row>
                <div className="col pt-3">
                  <CustomerInfoPanel
                    customerFirstName={benefit.firstName}
                    customerLastName={benefit.lastName}
                    serviceAddress={benefit.street}
                    serviceCity={benefit.city}
                    serviceState={benefit.state}
                    serviceZip={benefit.zip}
                  />
                </div>
                <div className="col pt-3">
                  <CurrentBenefitInfoPanel
                    currentDiscount={benefit.priorEnergyDiscount}
                    priorPledge={benefit.priorPledgeAmount}
                  />
                </div>
                <div className="col pt-3">
                  <AgencyInfoPanel
                    agencyName={benefit.agencyName}
                    agencyEmail={benefit.agencyEmail}
                    username={benefit.username}
                  />
                </div>
              </Row>
            </Card.Body>
          </Card>
          <ModifyBenefitFundTypeRenderer
            benefit={benefit}
            pledgeAmount={pledgeAmount}
            setFormField={handleFieldChange}
            incomeMeasurementType={incomeMeasurementType}
            incomeMeasurementPercentage={incomeMeasurementPercentage}
            arrearsLiabilityAmount={arrearsLiabilityAmount}
          />
          <Row>
            <Form.Group className="pt-3">
              <AdditionalInformationField
                value={additionalInformation}
                onChange={(value) => handleFieldChange('additionalInformation', value)}
                isSubmitting={saving}
              />
            </Form.Group>
          </Row>

          {hasChanges && (
            <>
              <hr />
              <Row>
                <Form.Group className="mb-3">
                  <Form.FloatingLabel label={<Required>Reason For Change</Required>} controlId="reason-for-change">
                  <Form.Control
                    as="textarea"
                    rows={5}
                    maxLength="100"
                    placeholder="Reason For Change"
                    style={{ height: '100px' }}
                    value={reasonForChange}
                    required
                    onChange={(e) => setReasonForChange(e.target.value)}
                  />
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Control.Feedback type="invalid">Reason For Change is required.</Form.Control.Feedback>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <div className="d-flex gap-2">
            <Button
              type="submit"
              variant="success"
              onClick={() => setStatus(BENEFIT_STATUS.APPROVED)}
              disabled={saving}
            >
              {saving && status === BENEFIT_STATUS.APPROVED ? 'Saving...' : getApproveButtonText()}
            </Button>
            <Button
              type="submit"
              variant="outline-warning"
              onClick={() => setStatus(BENEFIT_STATUS.HELD)}
              disabled={saving}
            >
              {saving && status === BENEFIT_STATUS.HELD ? 'Saving...' : getHoldButtonText()}
            </Button>
            <Button
              type="submit"
              variant="outline-danger"
              onClick={() => setStatus(BENEFIT_STATUS.REJECTED)}
              disabled={saving}
            >
              {saving && status === BENEFIT_STATUS.REJECTED ? 'Saving...' : 'Reject'}
            </Button>
          </div>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModifyBenefitModal;